import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { API_BASE_URL } from "../../config/api";

const formatCreatedDate = (value) => {
  if (!value) return "N/A";
  try {
    const date = new Date(value);
    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const formatMoney = (value, currency = "GBP") => {
  if (value == null || value === "") return "—";
  const amount = parseFloat(value);
  if (!Number.isFinite(amount)) return String(value);
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
    }).format(amount);
  } catch {
    return `£${amount.toFixed(2)}`;
  }
};

const statusColor = (status) => {
  const value = String(status || "").toUpperCase();
  if (value === "PAYMENT") return "success";
  if (value === "CANCELLED") return "error";
  if (value === "AMENDMENT") return "warning";
  return "default";
};

const TapBookings = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectionModel, setSelectionModel] = useState([]);

  const fetchTapBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/signup/getTapBookings`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch TAP bookings");
      }

      const data = await response.json();
      if (data.subCode === 200 && Array.isArray(data.items)) {
        setRows(
          data.items.map((item) => ({
            ...item,
            id: item._id,
          }))
        );
        setMessage(data.message || "");
      } else {
        setError("Failed to load TAP bookings: " + (data.message || "Unknown error"));
      }
    } catch (err) {
      setError(err.message);
      console.error("TAP booking fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTapBookings();
  }, []);

  const filteredRows = useMemo(() => {
    if (statusFilter === "ALL") return rows;
    return rows.filter(
      (row) => String(row.bookingStatus || "").toUpperCase() === statusFilter
    );
  }, [rows, statusFilter]);

  const handleExportSelected = () => {
    if (!selectionModel.length) {
      alert("Select at least one booking to export.");
      return;
    }
    const idSet = new Set(selectionModel);
    const selected = filteredRows.filter((row) => idSet.has(row.id));
    const exportData = selected.map((row) => ({
      "Create Date": formatCreatedDate(row.createdAt),
      Reference: row.referenceNumber ?? "",
      Status: row.bookingStatus ?? "",
      Name: row.name ?? "",
      Airport: row.airport ?? "",
      "Product Type": row.productType ?? "",
      "Product Name": row.productName ?? "",
      "Product Code": row.productCode ?? "",
      Arrival: row.arrivalDateTime ?? "",
      Return: row.returnDateTime ?? "",
      "Arrival Terminal": row.arrivalTerminal ?? "",
      "Return Terminal": row.returnTerminal ?? "",
      "Out Flight": row.outFlight ?? "",
      "Return Flight": row.rtnFlight ?? "",
      Reg: row.reg ?? "",
      Make: row.make ?? "",
      Model: row.model ?? "",
      Colour: row.colour ?? "",
      Passengers: row.passengers ?? "",
      Mobile: row.mobile ?? "",
      Currency: row.currency ?? "",
      "List Price": row.listPrice ?? "",
      "Amount Paid": row.amountPaid ?? "",
      "Supplier Cost": row.supplierCost ?? "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TAP Bookings");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `tap-bookings-${stamp}.xlsx`);
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Create Date",
      flex: 1.1,
      minWidth: 160,
      renderCell: (params) => formatCreatedDate(params.value),
    },
    {
      field: "referenceNumber",
      headerName: "Reference",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "bookingStatus",
      headerName: "Status",
      flex: 0.9,
      minWidth: 130,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value || "—"}
          color={statusColor(params.value)}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1.3,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "airport",
      headerName: "Airport",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "productType",
      headerName: "Product Type",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "productName",
      headerName: "Product Name",
      flex: 1.2,
      minWidth: 170,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "arrivalDateTime",
      headerName: "Arrival",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "returnDateTime",
      headerName: "Return",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "arrivalTerminal",
      headerName: "Arr. Terminal",
      flex: 0.9,
      minWidth: 120,
    },
    {
      field: "returnTerminal",
      headerName: "Rtn. Terminal",
      flex: 0.9,
      minWidth: 120,
    },
    {
      field: "outFlight",
      headerName: "Out Flight",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "rtnFlight",
      headerName: "Rtn Flight",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "reg",
      headerName: "Car Reg",
      flex: 0.8,
      minWidth: 110,
    },
    {
      field: "vehicle",
      headerName: "Vehicle",
      flex: 1.3,
      minWidth: 180,
      valueGetter: (params) =>
        [params.row.colour, params.row.make, params.row.model].filter(Boolean).join(" "),
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5 }}>
          {[params.row.colour, params.row.make, params.row.model].filter(Boolean).join(" ")}
        </Box>
      ),
    },
    {
      field: "passengers",
      headerName: "Pax",
      flex: 0.5,
      minWidth: 70,
    },
    {
      field: "mobile",
      headerName: "Mobile",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "listPrice",
      headerName: "List Price",
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => formatMoney(params.value, params.row.currency),
    },
    {
      field: "amountPaid",
      headerName: "Amount Paid",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => formatMoney(params.value, params.row.currency),
    },
    {
      field: "supplierCost",
      headerName: "Supplier Cost",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => formatMoney(params.value, params.row.currency),
    },
  ];

  if (loading) return <Box m="20px">Loading TAP bookings...</Box>;
  if (error) return <Box m="20px">Error: {error}</Box>;

  return (
    <Box m="20px">
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="flex-end"
        justifyContent="space-between"
        gap={2}
      >
        <Header
          title="TAP BOOKINGS"
          subtitle={message || "Managing TAP bookings"}
        />
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: "30px" }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setSelectionModel([]);
              }}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="PAYMENT">Payment</MenuItem>
              <MenuItem value="AMENDMENT">Amendment</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DownloadOutlinedIcon />}
            onClick={handleExportSelected}
            disabled={selectionModel.length === 0}
          >
            Export bookings
          </Button>
        </Box>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2} mb="20px">
        {[
          { label: "Total bookings", value: rows.length },
          { label: "Showing", value: filteredRows.length },
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{
              backgroundColor: colors.primary[400],
              px: 3,
              py: 2,
              minWidth: 160,
              borderRadius: "4px",
            }}
          >
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}>
              {stat.value}
            </Typography>
            <Typography variant="h6" color={colors.greenAccent[500]}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        m="20px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
            py: "8px",
            fontSize: "1rem",
            lineHeight: 1.5,
            color: colors.grey[100],
          },
          "& .MuiDataGrid-row": {
            minHeight: "90px !important",
            maxHeight: "none !important",
            height: "auto !important",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? `${colors.primary[500]} !important`
                  : `rgba(0, 0, 0, 0.04) !important`,
            },
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
            fontSize: "1rem",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: "1rem",
            },
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          checkboxSelection
          rows={filteredRows}
          columns={columns}
          autoHeight={false}
          disableRowSelectionOnClick
          disableColumnResize={false}
          selectionModel={selectionModel}
          onSelectionModelChange={(newModel) => setSelectionModel(newModel)}
          initialState={{
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default TapBookings;
